import React, { useEffect, useMemo, useState, useContext, useRef } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { DropdownFilter } from '../../../components/2025/DropdownFilter'
import qs from 'query-string'
import groupBy from 'lodash.groupby'
import { QUERY } from '../../../graphql'
import shuffle from 'lodash.shuffle'
import styled from 'styled-components'
import { GradCard } from '../../../components/2025/GradCard'
import { programGroups25 as programGroups, projectTypeMap25 } from '../../../constant' // Note: Using 2024 groups?
import { CSSTransition, TransitionGroup } from 'react-transition-group'
import { SpringContext } from '../../../context/SpringContext'
import { useViewport } from '../../../hook/useViewport'
import { ThemeContext } from '../../../context/2025/ThemeContext'
import { SearchContainer } from '../../../styles/2025/Styles'
// Removed unused 'use' import: import { use } from 'react'

const Header = React.lazy(() => import('../../../components/2025/Header'))
const Footer = React.lazy(() => import('../../../components/2025/Footer'))
const FullScreenLoader = React.lazy(() => import('../../../components/FullScreenLoader'))
const Pagination = React.lazy(() => import('../../../components/2025/Pagination'))


const TextCenterDiv = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-transform: uppercase;
    padding: 120px 0 60px 0;
`

const alphabet = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
  'O',
  'P',
  'Q',
  'R',
  'S',
  'T',
  'U',
  'V',
  'W',
  'X',
  'Y',
  'Z', // Corrected 'p' to 'P'
]

const ITEMS_PER_PAGE = 12

export default function Grads() {
  const location = useLocation()
  const { search: searchParams } = location
  const { program, type, page } = qs.parse(searchParams)
  const [gradsSearch, setGradsSearch] = useState('')
  // Optional: Debounce if needed
  // const [gradsSearchDebounce] = useDebounce(gradsSearch, 500);
  const spring = useContext(SpringContext)
  const { isDarkMode } = useContext(ThemeContext)
  const inputRef = useRef(null)
  // --- Step 1: Add ref to track user interaction ---
  const hasUserInteracted = useRef(false)
  const history = useHistory()
  const { width } = useViewport()
  const isMobile = width <= 640

  // Removed console.log for production code
  // useEffect(() => {
  //   console.log('Grads page mounted')
  // }, [])

  // useEffect(() => {
  //   if (spring != null && spring !== undefined) {
  //     spring.setCurrentValue(-1000).setAtRest()
  //     spring.setEndValue(-1000)
  //   }
  // }, [spring])

  const { data, loading } = useQuery(QUERY.USERS_QUERY, {
    variables: {
      where: {
        studentYear: 2025,
        activeAt_gt: '2024-12-31',
        // Use gradsSearch (or debounced version if implemented)
        nameSearch: gradsSearch,
      },
    },
    // Optional: onError: () => { hasUserInteracted.current = false; }
  })

  const grads = useMemo(() => {
    if (!data?.users) {
      return {}
    }
    let users = data?.users || []

    // Apply program filter
    if (program) {
      users = users.filter((user) => user.program === program)
    }

    // Apply type filter
    if (type) {
      // Removed console.log for production
      // console.log('TYPE: ', type)
      let typeSafe = type?.replace(' ', '_').replace('/', '_').toLowerCase()
      if (typeSafe === '3d_art_animation') {
        typeSafe = 'art_animation_3d'
      }
      const typeList = projectTypeMap25[typeSafe] || [] // Default to empty array if type not found
      if (typeList.length > 0) {
        // Only filter if typeList is valid
        users = users.filter((stu) => {
          const stdPrimaryExpertise = stu.primaryExpertise?.replace(' ', '_')?.toLowerCase() || ''
          const stdSecondaryExpertise =
            stu.secondaryExpertise?.replace(' ', '_')?.toLowerCase() || ''
          // Check if either expertise is included in the valid typeList
          return typeList.includes(stdPrimaryExpertise) || typeList.includes(stdSecondaryExpertise)
        })
      } else {
        // Handle case where type filter doesn't match anything? Maybe return no users or log warning.
        // users = []; // Example: show no users if filter is invalid
      }
    }

    // Group remaining users by program
    const grouped = groupBy(shuffle(users), 'program') // Shuffle before grouping

    // Ensure only valid program groups are included
    const obj = {}
    programGroups.forEach((key) => {
      if (grouped[key]?.length > 0) {
        obj[key] = grouped[key]
      }
    })

    return obj
  }, [data, program, type])

  const gradsSorted = useMemo(() => {
    let gradList = []
    // Flatten the grouped grads into a single list
    Object.values(grads).forEach((students) => {
      gradList = gradList.concat(students)
    })

    // Sort the combined list by first name
    gradList.sort((a, b) => {
      const textA = a.firstName?.toUpperCase() || ''
      const textB = b.firstName?.toUpperCase() || ''
      if (textA < textB) return -1
      if (textA > textB) return 1
      return 0 // Names are equal
    })

    return gradList
  }, [grads])

  // Pagination logic
  const currentPage = parseInt(page) || 1
  const totalPages = Math.ceil(gradsSorted.length / ITEMS_PER_PAGE)
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const endIndex = startIndex + ITEMS_PER_PAGE
  const paginatedGrads = gradsSorted.slice(startIndex, endIndex)

  const handlePageChange = (newPage) => {
    const params = qs.parse(location.search)
    const newSearch = qs.stringify({ ...params, page: newPage })
    history.push(`${location.pathname}?${newSearch}`)

    if (currentPage !== newPage) {
      // --- Step 2: Set interaction flag on pagination (Optional) ---
      // hasUserInteracted.current = true; // If pagination causes loading and focus is desired
      if (spring) {
        spring.setCurrentValue(900).setAtRest()
        spring.setEndValue(900)
      }
    }
  }

  // Effect to reset page when filters/search change
  useEffect(() => {
    const params = qs.parse(location.search)
    const shouldResetPage = params.page && params.page !== '1' // Simpler check: reset if page exists and is not 1

    if (shouldResetPage) {
      // --- Step 2: Mark interaction when filters change (Optional) ---
      // hasUserInteracted.current = true; // If filter changes cause loading and focus is desired
      history.replace({
        pathname: location.pathname,
        search: qs.stringify({ ...params, page: 1 }),
      })
    }
    // Rerun when search, filters, or total items change (affecting pagination)
  }, [gradsSearch, program, type, history, location.pathname, gradsSorted.length])

  // --- Step 3: Modify the focus effect ---
  useEffect(() => {
    // Only focus if:
    // 1. Loading has finished (!loading)
    // 2. The input ref exists (inputRef.current)
    // 3. User interaction flag is true (hasUserInteracted.current)
    if (!loading && inputRef.current && hasUserInteracted.current) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          inputRef.current.focus({ preventScroll: true })
          // --- Step 4: Reset the flag after focusing ---
          hasUserInteracted.current = false
        })
      })
    } else if (!loading && !hasUserInteracted.current) {
      // Optional explicit blur
      // if (document.activeElement === inputRef.current) {
      //     inputRef.current.blur();
      // }
    }
  }, [loading]) // Only depends on loading

  // --- Step 2: Create handler for search input change ---
  const handleSearchChange = (e) => {
    const value = e.target.value
    setGradsSearch(value)
    // Set interaction flag whenever the user types
    hasUserInteracted.current = true
  }

  require('./grads.scss')

  return (
    <div
      // Added min-h-screen back and fixed flex class concatenation
      className={`absolute w-full min-h-screen ${
        isDarkMode ? 'bg-black' : 'bg-white'
      } flex flex-col items-center justify-start pb-20 lg:pb-0`}
    >
      {loading && (
        <FullScreenLoader
          color={isDarkMode ? `#DAFF01` : `#9167F0`}
          style={{ position: 'fixed' }}
        />
      )}
      {Header && <Header />}

      {/* Header Section */}
      <section
        className={`w-full ${isDarkMode ? 'bg-black' : 'bg-white'} transition-colors duration-300 `}
      >
        <div className='max-w-[1440px] mx-auto px-6 lg:px-[48px] py-32 flex flex-col lg:flex-row items-center justify-center gap-32'>
          <TextCenterDiv className=' px-[16px] lg:px-[48px]'>
            <h1
              className={`${isMobile ? 'heading-1-mobile !text-[50px]' : 'heading-1 '} ${
                isDarkMode ? 'text-lime' : 'text-lavender'
              } `}
              style={{ pointerEvents: 'none' }}
            >
              Graduates
            </h1>
            {isMobile ? (
              <div className='text-center pt-[16px]'>
                <h3
                  className={`heading-3b ${
                    isDarkMode ? 'text-white' : 'text-black'
                  } normal-case m-0`}
                  style={{ pointerEvents: 'none' }}
                >
                  YES! We're innovative
                </h3>
                <h3
                  className={`heading-3b ${
                    isDarkMode ? 'text-white' : 'text-black'
                  } normal-case m-0`}
                  style={{ pointerEvents: 'none' }}
                >
                  problem solvers!
                </h3>
              </div>
            ) : (
              <h3
                className={`heading-3b ${isDarkMode ? 'text-white' : 'text-black'} normal-case m-0`}
                style={{ pointerEvents: 'none' }}
              >
                YES! We're innovative problem solvers!
              </h3>
            )}
          </TextCenterDiv>
        </div>
      </section>

      {/* Filter Section */}
      <section
        className={`w-full ${isDarkMode ? 'bg-black' : 'bg-white'} transition-colors duration-300 `}
      >
        <div className='max-w-[1440px] w-full mx-auto px-6 lg:px-[48px] py-16 flex flex-col lg:flex-row items-center justify-between gap-16'>
          {' '}
          {/* Adjusted padding */}
          <div className='w-full flex gap-6 px-[16px] lg:px-[48px]'>
            {/* Pass setGradsSearch to DropdownFilter if it needs to update the search term */}
            {/* If DropdownFilter only handles program/type, this is fine */}
            <DropdownFilter program={program} type={type} onChangeSearchGrads={setGradsSearch} />
          </div>
        </div>
      </section>

      {/* Count and Search Input Section */}
      <section
        className={`w-full ${
          isDarkMode ? 'bg-[#222222]' : 'bg-grey-25'
        } transition-colors duration-300 `}
      >
        <div className='max-w-[1440px] mx-auto px-6 lg:px-[48px] flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-32 py-16'>
          {' '}
          {/* Adjusted padding/gap */}
          <div
            className={`grid grid-cols-1 lg:grid-cols-2 w-full items-center ${
              isDarkMode ? 'bg-[#222222]' : 'bg-grey-25'
            } px-[16px] lg:px-[48px]`}
          >
            {/* Grad Count Display */}
            <div>
              {!loading && gradsSorted?.length === 0 ? (
                <p className={`heading-4 ${isDarkMode ? 'text-white' : 'text-black'}`}>
                  No graduates found matching your criteria.
                </p>
              ) : !loading && gradsSorted?.length === 1 ? (
                <p className={`heading-4 ${isDarkMode ? 'text-white' : 'text-black'}`}>
                  There is <span className={isDarkMode ? 'text-lime' : 'text-lavender'}>1</span>{' '}
                  graduate.
                </p>
              ) : !loading && gradsSorted?.length > 1 ? (
                <p className={`heading-4 ${isDarkMode ? 'text-white' : 'text-black'}`}>
                  There are{' '}
                  <span className={isDarkMode ? 'text-lime' : 'text-lavender'}>
                    {gradsSorted?.length}
                  </span>{' '}
                  graduates.
                </p>
              ) : loading ? (
                <p className={`heading-4 ${isDarkMode ? 'text-white' : 'text-black'}`}>
                  Loading graduates...
                </p>
              ) : null}
            </div>

            {/* Search Input */}
            <SearchContainer
              className='flex relative w-full items-end justify-end' // Adjusted classes if needed
              isDarkMode={isDarkMode}
              // Remove hasError/submitted unless used by SearchContainer
            >
              <input
                type='text'
                id='gname'
                name='gname'
                ref={inputRef} // Keep ref attached
                placeholder='Search graduates'
                className={`body-text placeholder-black w-full lg:w-4/5 ${
                  isDarkMode ? 'bg-white text-black' : 'bg-white text-black'
                }`} // Style adjustments
                value={gradsSearch}
                onChange={handleSearchChange} // Use the interaction handler
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault() // Prevent form submission
                    // hasUserInteracted.current = true; // Optionally set flag on Enter
                  }
                }}
                // Ensure no autoFocus={true}
              />
              {/* Search Icon SVG */}
              <svg
                width='26'
                height='26'
                viewBox='0 0 26 26'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                className={`absolute pointer-events-none ${isDarkMode ? 'right-2' : 'right-2'}`} // Adjust positioning
              >
                <g id='Frame 76'>
                  <path
                    id='Vector'
                    d='M0.93934 22.9393C0.353553 23.5251 0.353553 24.4749 0.93934 25.0607C1.52513 25.6464 2.47487 25.6464 3.06066 25.0607L0.93934 22.9393ZM22.5 9.7C22.5 13.1242 19.7242 15.9 16.3 15.9V18.9C21.381 18.9 25.5 14.781 25.5 9.7H22.5ZM16.3 15.9C12.8758 15.9 10.1 13.1242 10.1 9.7H7.1C7.1 14.781 11.219 18.9 16.3 18.9V15.9ZM10.1 9.7C10.1 6.27584 12.8758 3.5 16.3 3.5V0.5C11.219 0.5 7.1 4.61898 7.1 9.7H10.1ZM16.3 3.5C19.7242 3.5 22.5 6.27583 22.5 9.7H25.5C25.5 4.61898 21.381 0.5 16.3 0.5V3.5ZM3.06066 25.0607L7.56066 20.5607L5.43934 18.4393L0.93934 22.9393L3.06066 25.0607ZM7.56066 20.5607L12.0607 16.0607L9.93934 13.9393L5.43934 18.4393L7.56066 20.5607Z'
                    fill={isDarkMode ? '#FFFFFF' : '#000000'} // Icon color based on mode
                  />
                </g>
              </svg>
            </SearchContainer>
          </div>
        </div>
      </section>

      {/* Grad Grid Section */}
      <section
        className={`w-full ${
          isDarkMode ? 'bg-[#222222]' : 'bg-grey-25'
        } transition-colors duration-300 `}
      >
        <div className='max-w-[1440px] mx-auto px-6 lg:px-[48px] py-16 flex flex-col items-center justify-between gap-16'>
          {' '}
          {/* Adjusted padding/gap */}
          <TransitionGroup
            className={`grid grid-cols-1 lg:grid-cols-3 gap-16 w-full ${
              /* Removed padding here */ ''
            } ${isDarkMode ? 'bg-[#222222]' : 'bg-grey-25'}`}
          >
            {!loading &&
              paginatedGrads?.length > 0 &&
              paginatedGrads.map((grad) => (
                <CSSTransition key={grad.id} timeout={500} classNames='item'>
                  {/* Pass isDarkMode to GradCard */}
                  <GradCard grad={grad} isDarkMode={isDarkMode} />
                </CSSTransition>
              ))}
          </TransitionGroup>
        </div>
      </section>

      {/* Pagination Section */}
      {totalPages > 1 && (
        <section
          className={`w-full ${
            isDarkMode ? 'bg-[#222222]' : 'bg-light-grey-25'
          } transition-colors duration-300 `}
        >
          <div className='max-w-[1440px] mx-auto px-6 lg:px-[48px] py-16 flex flex-col items-center justify-between gap-16'>
            {' '}
            {/* Adjusted padding */}
            <Pagination
              totalPages={totalPages}
              currentPage={currentPage}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={handlePageChange}
              // Pass isDarkMode if needed
              // isDarkMode={isDarkMode}
            />
          </div>
        </section>
      )}

      {/* Footer Section */}
      <div
        className={`relative w-full flex flex-col items-center justify-center w-full ${
          isDarkMode ? 'bg-black' : 'bg-light-grey-25'
        }`}
      >
        <div
          className={`relative bottom-0 w-full pt-16 px-[16px] lg:px-[48px] bg-black max-w-[1440px] ${
            isDarkMode ? 'bg-black' : 'bg-light-grey-25'
          }`}
        >
          {Footer && <Footer />}
        </div>
      </div>
    </div>
  )
}
